import { apiCall, ApiError } from '@/lib/api-client';
import type {
  CloseNegativeResult,
  CloserCase,
  CloserScope,
  CloserSummary,
  CloserTarget,
  ForwardBody,
  ForwardResult,
  NotInFindhelpResult,
} from './closer.types';

/**
 * La API de la pantalla del closer (CONTRATO-CLOSER §3). Todas las rutas son
 * del rol `navigator`; las de trabajo exigen además `navigator_scope='closer'`
 * del lado del servidor (403 `NOT_A_CLOSER`), que lee la base y no el token.
 */
export const CLOSER_KEYS = {
  /** La compuerta: la misma clave para el shell viejo y para el nuevo. */
  scope: ['navigator', 'scope'] as const,
  summary: ['closer', 'summary'] as const,
  next: ['closer', 'next'] as const,
  targets: ['closer', 'targets'] as const,
};

export const closerApi = {
  scope: () => apiCall<CloserScope>('/navigator/closer/scope'),
  summary: () => apiCall<CloserSummary>('/navigator/closer/summary'),
  /** 204 = no hay casos: React Query no admite `undefined`, así que es null. */
  next: async (): Promise<CloserCase | null> => {
    const c = await apiCall<CloserCase | undefined>('/navigator/closer/next');
    return c ?? null;
  },
  closeNegative: (caseId: string) =>
    apiCall<CloseNegativeResult>(`/navigator/closer/cases/${caseId}/close-negative`, { method: 'POST' }),
  notInFindhelp: (caseId: string) =>
    apiCall<NotInFindhelpResult>(`/navigator/closer/cases/${caseId}/not-in-findhelp`, { method: 'POST' }),
  targets: () => apiCall<CloserTarget[]>('/navigator/closer/targets'),
  forward: (caseId: string, body: ForwardBody) =>
    apiCall<ForwardResult>(`/navigator/closer/cases/${caseId}/forward`, { method: 'POST', body }),
};

/**
 * El código tipado del error. El filtro global de la API envuelve TODO error:
 * el cuerpo real es `{ statusCode, timestamp, path, message: { code, message,
 * ...extras } }` (CONTRATO-CLOSER §3, corregido por el coordinador el 24-oct).
 * Por eso se lee primero `payload.message.code`, y `payload.code` queda solo
 * de respaldo. Sin código, null: la pantalla lo trata como un error genérico y
 * ofrece «Reintentar», nunca avanza sola.
 */
export function closerErrorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const p = err.payload as { code?: unknown; message?: unknown } | null;
  const inner = p?.message;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const c = (inner as { code?: unknown }).code;
    if (typeof c === 'string' && c) return c;
  }
  if (p && typeof p.code === 'string' && p.code) return p.code;
  return null;
}

export function closerErrorStatus(err: unknown): number | null {
  return err instanceof ApiError ? err.status : null;
}
